import { ArrowLeft, KeyRound, MoreHorizontal, Power, PowerOff, Settings2 } from "lucide-react";
import * as React from "react";
import { Link, useParams } from "react-router-dom";
import { Page, PageHeader } from "@/components/layout/page";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { useToast } from "@/components/ui/toast";
import { TriggerFormDialog } from "@/features/trigger-form";
import { TriggerRequestsCard } from "@/features/trigger-requests";
import { TriggerSecretDialog, type ShownTriggerSecret } from "@/features/trigger-secret-dialog";
import { TriggerSetupCard } from "@/features/trigger-setup";
import { ApiError } from "@/lib/api";
import { useCampaigns, useRotateTriggerSecret, useTrigger, useUpdateTrigger } from "@/lib/queries";
import { actionTerm } from "@/lib/triggers";

/**
 * One trigger (ADR-021): how to call it, and everything it received with what each request made.
 *
 * The page is where a sender is set up. Send one request, from a zap's test or the command in "How
 * to call it", and it shows up under Requests within seconds, with the paths a mapping can name in
 * it: fix the mapping from those, then apply the request again.
 */
export function TriggerPage() {
  const { triggerId } = useParams();
  const id = Number(triggerId);
  const valid = Number.isInteger(id) && id > 0;
  const toast = useToast();
  const trigger = useTrigger(valid ? id : undefined);
  const campaigns = useCampaigns();
  const update = useUpdateTrigger();
  const rotate = useRotateTriggerSecret();
  const [editing, setEditing] = React.useState(false);
  const [rotating, setRotating] = React.useState(false);
  const [secret, setSecret] = React.useState<ShownTriggerSecret>();

  if (valid && trigger.isPending) {
    return (
      <Page>
        <div className="flex min-h-[40vh] items-center justify-center">
          <Spinner className="size-5" />
        </div>
      </Page>
    );
  }

  if (!valid || trigger.error || !trigger.data) {
    const missing = !valid || (trigger.error instanceof ApiError && trigger.error.status === 404);
    return (
      <Page>
        {missing ? (
          <EmptyState
            className="min-h-[60vh]"
            title="Trigger not found"
            description="It belongs to another organisation, or the address is wrong."
            action={
              <Button variant="outline" asChild>
                <Link to="/triggers">Back to triggers</Link>
              </Button>
            }
          />
        ) : (
          <ErrorState
            className="min-h-[60vh]"
            error={trigger.error}
            title="Could not load the trigger"
            action={
              <Button variant="outline" onClick={() => void trigger.refetch()}>
                Try again
              </Button>
            }
          />
        )}
      </Page>
    );
  }

  const t = trigger.data;
  const campaign = t.campaign_id ? (campaigns.data?.find((c) => c.id === t.campaign_id)?.name ?? "A campaign") : null;

  const setOn = async (enabled: boolean) => {
    try {
      await update.mutateAsync({ id: t.id, enabled });
      toast.success(enabled ? "Trigger on" : "Trigger off", t.name);
    } catch (e) {
      toast.error(
        enabled ? "Could not turn it on" : "Could not turn it off",
        e instanceof ApiError ? e.message : "Please try again.",
      );
    }
  };

  return (
    <Page>
      <Link to="/triggers" className="mb-3 inline-flex items-center gap-1 text-xs text-text-muted hover:text-text">
        <ArrowLeft className="size-3.5" aria-hidden />
        Triggers
      </Link>

      <PageHeader
        title={
          <span className="inline-flex flex-wrap items-center gap-2">
            {t.name}
            <Badge tone="accent">{actionTerm(t.action).label}</Badge>
            {!t.enabled && <Badge tone="warning">Off</Badge>}
          </span>
        }
        description={
          <>
            {actionTerm(t.action).description}
            {t.action !== "capture" && (
              <span className="block text-xs">{campaign ? `Campaign: ${campaign}` : "Every campaign the lead is on."}</span>
            )}
            {t.description && <span className="block text-xs">{t.description}</span>}
          </>
        }
        action={
          <>
            <Button variant="outline" onClick={() => setEditing(true)}>
              <Settings2 />
              Edit
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="More actions">
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {t.enabled ? (
                  <DropdownMenuItem onSelect={() => void setOn(false)}>
                    <PowerOff />
                    Turn off
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onSelect={() => void setOn(true)}>
                    <Power />
                    Turn on
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => setRotating(true)}>
                  <KeyRound />
                  Rotate the secret
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      <div className="space-y-4">
        {!t.enabled && (
          <p className="rounded-md border border-warning-4 bg-warning-2 px-3 py-2 text-xs text-warning-11">
            This trigger is off: its URL answers 404, and nothing a sender sends is kept.
          </p>
        )}
        <TriggerSetupCard trigger={t} />
        <TriggerRequestsCard trigger={t} />
      </div>

      <TriggerFormDialog open={editing} onOpenChange={setEditing} trigger={t} />

      <TriggerSecretDialog secret={secret} onClose={() => setSecret(undefined)} />

      <ConfirmDialog
        open={rotating}
        onOpenChange={setRotating}
        title="Rotate the secret?"
        description={
          <>
            <span className="font-medium">{t.name}</span> refuses a request with the old secret from the moment the
            new one is made. Put the new one in the sender's headers straight away.
          </>
        }
        confirmLabel="Rotate secret"
        loading={rotate.isPending}
        onConfirm={async () => {
          try {
            const rotated = await rotate.mutateAsync(t.id);
            setRotating(false);
            setSecret({
              triggerId: t.id,
              name: t.name,
              path: t.path,
              header: rotated.secret_header,
              secret: rotated.secret,
              rotated: true,
            });
          } catch (e) {
            toast.error("Could not rotate the secret", e instanceof ApiError ? e.message : "Please try again.");
          }
        }}
      />
    </Page>
  );
}
